/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useRef, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import A_HTML_CANVAS from "./A_HTML_CANVAS";

function A_ARROW_PERPENDICULAR({
  layer,
  long, // Anchor Point (Feature Location)
  lat,
  arrow_width = 0.001, // Degree value representing the horizontal gap scale
  arrow_height = 0.001, // Degree value representing the vertical gap scale
  box_position = "top_right",
  corner_mode,
  arrow_style = "triangle",
  title,
  desc,
  long_width, // Box Width in Degrees (controls Scale)
  lat_width, // Box Height in Degrees (controls Aspect Ratio)
  image_1,
  image_2,
}) {
  const [attached, set_attached] = useState(false);
  const [layout, setLayout] = useState(null);
  const [isVisible, setIsVisible] = useState(true);

  const has_dimensions = long_width && lat_width;

  // Font scaling
  const base_scale_factor = 100;
  const proportional_title_size = long_width * base_scale_factor * 0.01;
  const proportional_desc_size = long_width * base_scale_factor * 0.007;

  // Refs
  const anchor_ref = useRef([long, lat]);
  const dim_ref = useRef({ w: long_width, h: lat_width });
  const arrow_ref = useRef({ w: Number(arrow_width), h: Number(arrow_height) });
  const style_ref = useRef({
    arrow: arrow_style,
    corner: corner_mode,
    pos: box_position,
  });

  const isVisibleRef = useRef(true);
  const canvas_ref = useRef(null);
  const render_handle_ref = useRef(null);
  const map_object_ref = useRef(layer?.map_object || null);

  useEffect(() => {
    map_object_ref.current = layer?.map_object || null;
  }, [layer?.map_object]);

  useEffect(() => {
    anchor_ref.current = [long, lat];
    dim_ref.current = { w: long_width, h: lat_width };
    arrow_ref.current = { w: Number(arrow_width), h: Number(arrow_height) };
  }, [long, lat, long_width, lat_width, arrow_width, arrow_height]);

  useEffect(() => {
    style_ref.current = {
      arrow: arrow_style,
      corner: corner_mode,
      pos: box_position,
    };
  }, [arrow_style, corner_mode, box_position]);

  const drawArrowHead = useCallback(
    (ctx, tipx, tipy, ux, uy, hpx, hpy, hl, hw, arrowType) => {
      if (arrowType === "none") return;
      const basex = tipx - ux * hl;
      const basey = tipy - uy * hl;
      const lpx = basex + hpx * (hw / 2);
      const lpy = basey + hpy * (hw / 2);
      const rpx = basex - hpx * (hw / 2);
      const rpy = basey - hpy * (hw / 2);

      ctx.beginPath();
      if (arrowType === "triangle") {
        ctx.moveTo(tipx, tipy);
        ctx.lineTo(lpx, lpy);
        ctx.lineTo(rpx, rpy);
        ctx.closePath();
        ctx.fill();
      } else if (arrowType === "circle") {
        ctx.arc(tipx, tipy, hw / 2, 0, Math.PI * 2);
        ctx.fill();
      } else if (arrowType === "diamond") {
        const backx = tipx - ux * (hl * 1.5);
        const backy = tipy - uy * (hl * 1.5);
        const midpx = tipx - ux * (hl * 0.75);
        const midpy = tipy - uy * (hl * 0.75);
        const dlx = midpx + hpx * (hw / 2);
        const dly = midpy + hpy * (hw / 2);
        const drx = midpx - hpx * (hw / 2);
        const dry = midpy - hpy * (hw / 2);
        ctx.moveTo(tipx, tipy);
        ctx.lineTo(dlx, dly);
        ctx.lineTo(backx, backy);
        ctx.lineTo(drx, dry);
        ctx.closePath();
        ctx.fill();
      }
    },
    []
  );

  const draw = useCallback(() => {
    const map = map_object_ref.current;
    const canvas = canvas_ref.current;
    if (!map || !canvas) return;

    // --- 1. Setup Canvas ---
    const dpr = window.devicePixelRatio || 1;
    const rect = map.getCanvas().getBoundingClientRect();
    const tw = Math.round(rect.width * dpr);
    const th = Math.round(rect.height * dpr);

    if (canvas.width !== tw || canvas.height !== th) {
      canvas.width = tw;
      canvas.height = th;
    }

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    ctx.scale(dpr, dpr);

    // --- 2. Calculate "Scale" (Pixels per Degree) ---
    const s = anchor_ref.current;
    const sp = map.project(s); // Anchor Screen Point (px)

    // Calculate how many pixels the 'long_width' occupies at this zoom level
    const p_ref = map.project([s[0] + dim_ref.current.w, s[1]]);
    const scale_w_px = Math.hypot(p_ref.x - sp.x, p_ref.y - sp.y);

    // --- 3. Determine Dimensions in Pixels (Static Ratio) ---
    const box_px_w = scale_w_px;

    // We force height based on input ratio to avoid tilt distortion
    const ratio = dim_ref.current.h / dim_ref.current.w;
    const box_px_h = box_px_w * ratio;

    // Apply the pixel scale derived from Width to both
    const arrow_px_w = box_px_w * (arrow_ref.current.w / dim_ref.current.w);
    const arrow_px_h = box_px_w * (arrow_ref.current.h / dim_ref.current.w);

    // --- 4. Position Box (Screen Space Math) ---
    // ep = End Point (Center of Box)
    let ep_x = sp.x;
    let ep_y = sp.y;

    const hw = box_px_w / 2;
    const hh = box_px_h / 2;
    const pos = style_ref.current.pos;

    // Screen Y is inverted (0 is top), so "Up" is subtraction
    switch (pos) {
      case "top":
        ep_y = sp.y - (arrow_px_h + hh);
        break;
      case "bottom":
        ep_y = sp.y + (arrow_px_h + hh);
        break;
      case "right":
        ep_x = sp.x + (arrow_px_w + hw);
        break;
      case "left":
        ep_x = sp.x - (arrow_px_w + hw);
        break;
      case "top_right":
        ep_y = sp.y - (arrow_px_h + hh);
        ep_x = sp.x + (arrow_px_w + hw);
        break;
      case "top_left":
        ep_y = sp.y - (arrow_px_h + hh);
        ep_x = sp.x - (arrow_px_w + hw);
        break;
      case "bottom_right":
        ep_y = sp.y + (arrow_px_h + hh);
        ep_x = sp.x + (arrow_px_w + hw);
        break;
      case "bottom_left":
        ep_y = sp.y + (arrow_px_h + hh);
        ep_x = sp.x - (arrow_px_w + hw);
        break;
      default:
        break;
    }

    // --- 5. Sync Layout ---
    if (
      !layout ||
      Math.abs(layout.cx - ep_x) > 1 ||
      Math.abs(layout.cy - ep_y) > 1 ||
      Math.abs(layout.w - box_px_w) > 1
    ) {
      setLayout({
        cx: ep_x,
        cy: ep_y,
        w: box_px_w,
        h: box_px_h,
      });
    }

    // Visibility Check
    if (box_px_w < 20) {
      if (isVisibleRef.current) {
        isVisibleRef.current = false;
        setIsVisible(false);
      }
      ctx.restore();
      return;
    } else if (!isVisibleRef.current) {
      isVisibleRef.current = true;
      setIsVisible(true);
    }

    // --- 6. Draw Arrow ---
    const b_left = ep_x - hw;
    const b_right = ep_x + hw;
    const b_top = ep_y - hh;
    const b_bottom = ep_y + hh;

    let mid_x, mid_y;
    // Determine the Elbow of the L-shape
    if (style_ref.current.corner === "horizontal-first") {
      mid_x = ep_x;
      mid_y = sp.y;
    } else {
      mid_x = sp.x;
      mid_y = ep_y;
    }

    let final_x = ep_x;
    let final_y = ep_y;
    const dx = ep_x - mid_x;
    const dy = ep_y - mid_y;

    if (Math.abs(dx) > Math.abs(dy)) {
      final_x = dx > 0 ? b_left : b_right;
    } else {
      final_y = dy > 0 ? b_top : b_bottom;
    }

    const base_dim_px = (box_px_w + box_px_h) / 2;
    const lw = Math.max(1, base_dim_px * 0.02);
    const arrow_len = Math.max(5, base_dim_px * 0.15);
    const arrow_wid = Math.max(4, base_dim_px * 0.15);

    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111";
    ctx.fillStyle = "#111";
    ctx.lineWidth = lw;

    // Draw Line
    ctx.beginPath();
    ctx.moveTo(sp.x, sp.y);
    if (Math.abs(mid_x - sp.x) > 1 || Math.abs(mid_y - sp.y) > 1) {
      ctx.lineTo(mid_x, mid_y);
    }
    ctx.lineTo(final_x, final_y);
    ctx.stroke();

    // --- FIX: Vector Calculation ---
    // Calculate direction from SP (Start Point) to the next point (Mid or Final)
    let start_dx = mid_x - sp.x;
    let start_dy = mid_y - sp.y;

    // If mid point is virtually the same as start point (Straight line case, e.g. Top/Bottom),
    // use the final point to determine direction.
    if (Math.abs(start_dx) < 1 && Math.abs(start_dy) < 1) {
      start_dx = final_x - sp.x;
      start_dy = final_y - sp.y;
    }

    const start_len = Math.hypot(start_dx, start_dy) || 1;
    const start_ux = start_dx / start_len;
    const start_uy = start_dy / start_len;

    // Draw Arrow Head (Reverse direction: Arrow points AT the feature)
    drawArrowHead(
      ctx,
      sp.x,
      sp.y,
      -start_ux,
      -start_uy,
      start_uy,
      -start_ux,
      arrow_len,
      arrow_wid,
      style_ref.current.arrow
    );

    ctx.restore();
  }, [layout]);

  const attach = useCallback(() => {
    const map = map_object_ref.current;
    if (!map || attached || !has_dimensions) return;
    if (!canvas_ref.current) {
      const c = document.createElement("canvas");
      Object.assign(c.style, {
        position: "absolute",
        top: "0",
        left: "0",
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 2,
      });
      canvas_ref.current = c;
    }
    if (!canvas_ref.current.parentNode)
      map.getCanvasContainer().appendChild(canvas_ref.current);
    if (!render_handle_ref.current) render_handle_ref.current = () => draw();

    map.on("render", render_handle_ref.current);
    map.on("resize", render_handle_ref.current);
    map.on("zoom", render_handle_ref.current);
    map.on("move", render_handle_ref.current);
    set_attached(true);
    draw();
  }, [attached, draw, has_dimensions]);

  const detach = useCallback(() => {
    const map = map_object_ref.current;
    if (map && render_handle_ref.current) {
      map.off("render", render_handle_ref.current);
      map.off("resize", render_handle_ref.current);
      map.off("zoom", render_handle_ref.current);
      map.off("move", render_handle_ref.current);
    }
    if (canvas_ref.current?.parentNode)
      canvas_ref.current.parentNode.removeChild(canvas_ref.current);
    canvas_ref.current = null;
    render_handle_ref.current = null;
    set_attached(false);
  }, []);

  useEffect(() => {
    if (map_object_ref.current && !attached) attach();
    return () => detach();
  }, []);

  useEffect(() => {
    const next_map = map_object_ref.current;
    if (next_map && !attached && has_dimensions) attach();
    if ((!next_map && attached) || (attached && !has_dimensions)) detach();
  }, [layer?.map_object, attached, attach, detach, has_dimensions]);

  if (!has_dimensions) return null;

  return (
    <>
      {isVisible && layout && (
        <A_HTML_CANVAS
          center_x={layout.cx}
          center_y={layout.cy}
          width={layout.w}
          height={layout.h}
          title={title}
          desc={desc}
          font_size_title={proportional_title_size}
          font_size_desc={proportional_desc_size}
          image_1={image_1}
          image_2={image_2}
        />
      )}
    </>
  );
}

const mapStateToProps = (state) => ({ layer: state.layer });
export default connect(mapStateToProps)(A_ARROW_PERPENDICULAR);
